import { useEffect, useRef, useState } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, X, UserPlus, Info, ExternalLink } from "lucide-react";
import { Button } from "@/components/shadcn/button";
import { Input } from "@/components/common/Input";
import { Label } from "@/components/common/FormLabel";
import { ErrorLabel } from "@/components/common/ErrorLabel";
import { Spinner } from "@/components/common/Loading/Spinner";
import { fetchActivities, fetchContactsByCompany, saveQuickTask } from "@/api/quickTask/api";
import { searchAutocomplete, findForMultipleCompanies } from "@/api/autocomplete/api";
import { quickTaskStorage } from "@/utils/quickTaskStorage";
import { useClassNames } from "@/hooks/useClassNames";
import type { ActivitiesResponse, Contact } from "@/types/domain";
import type { PanelState } from "./types";
import { useLayout } from "@/contexts/LayoutContext";
import { QuickProspectModal } from "@/components/common/QuickProspectModal";
import { QuickContactModal } from "@/components/common/QuickContactModal";
import { CompanyDetailsModal } from "@/components/common/CompanyDetailsModal";
import { ContactDetailsModal } from "@/components/common/ContactDetailsModal";
import { TextareaField } from "@/components/common/FormFields/TextareaField";

type QuickTaskPanelProps = {
  panelState: PanelState;
  hasEverOpened: boolean;
  onOpen: () => void;
  onMinimise: () => void;
  onClose: () => void;
};

type SelectOption = {
  value: string;
  label: string;
}

type GroupedOption = {
  label: string;
  options: SelectOption[];
}

type FormValues = {
  subject: string;
  body: string;
  activityId: string;
  companyId: string | null;
  companyName: string;
  contactId: string | null;
};

export const QuickTaskPanel = ({ panelState, hasEverOpened, onOpen, onMinimise, onClose }: QuickTaskPanelProps) => {
  const { data } = useLayout();
  const storage = quickTaskStorage();
  const initialValues = useRef(storage.getStoredFormValues());
  const [savedTaskId, setSavedTaskId] = useState<string | null>(
    initialValues.current.taskId || null
  );
  const [activityGroups, setActivityGroups] = useState<GroupedOption[]>([]);
  const [calendarSyncActivityIds, setCalendarSyncActivityIds] = useState<string[]>([]);
  const [contactGroups, setContactGroups] = useState<GroupedOption[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<SelectOption | null>(
    initialValues.current.customerId
      ? { value: initialValues.current.customerId, label: initialValues.current.customerName }
      : null
  );
  const [selectedContact, setSelectedContact] = useState<SelectOption | null>(null);
  const recentCompaniesRef = useRef<SelectOption[] | null>(null);
  const [companyDefaultOptions, setCompanyDefaultOptions] = useState<GroupedOption[] | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [isAddProspectOpen, setIsAddProspectOpen] = useState(false);
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [isCompanyDetailsOpen, setIsCompanyDetailsOpen] = useState(false);
  const [isContactDetailsOpen, setIsContactDetailsOpen] = useState(false);
  const defaultActivityIdRef = useRef<string>("");
  const [activitiesLoaded, setActivitiesLoaded] = useState(false);
  const [contactsLoaded, setContactsLoaded] = useState(!initialValues.current.customerId);
  const isFormLoading = !activitiesLoaded || !contactsLoaded;

  const { register, handleSubmit, control, setValue, reset, formState: { errors } } = useForm<FormValues>({
    defaultValues: {
      subject: initialValues.current.subject,
      body: initialValues.current.body || "",
      activityId: initialValues.current.activityId || "",
      companyId: initialValues.current.customerId || null,
      companyName: initialValues.current.customerName,
      contactId: initialValues.current.contactId || null,
    },
  });

  const activityId = useWatch({ control, name: "activityId" });
  const showCalendarSyncNotice = !savedTaskId && calendarSyncActivityIds.includes(activityId);

  // Load activities only when the panel is first opened
  useEffect(() => {
    if (!hasEverOpened) return;
    fetchActivities().then((result: ActivitiesResponse) => {
      const grouped: GroupedOption[] = result.groups.map((g) => ({
        label: g.groupName || "",
        options: g.items.map((item) => ({ value: item.id, label: item.description })),
      }));
      setActivityGroups(grouped);
      setCalendarSyncActivityIds(result.groups.flatMap((g) =>
        g.items.filter((item) => item.syncWithCalendar).map((item) => item.id)
      ));
      defaultActivityIdRef.current = result.defaultActivityId;
      // Only override the form value if nothing was restored from localStorage
      if (!initialValues.current.activityId) {
        setValue("activityId", defaultActivityIdRef.current);
        if (!initialValues.current.subject) {
          const defaultLabel = grouped.flatMap((g) => g.options).find((o) => o.value === result.defaultActivityId)?.label ?? "";
          if (defaultLabel) {
            setValue("subject", defaultLabel);
            storage.saveFieldValue(storage.KEYS.subject, defaultLabel);
          }
        }
      }
      setActivitiesLoaded(true);
    }).catch(() => {
      toast.error("Failed to load activities.");
      setActivitiesLoaded(true);
    });
  }, [hasEverOpened]);

  const loadContactsForCompany = (companyId: string | null) => {
    if (!companyId) {
      setContactGroups([]);
      setSelectedContact(null);
      setValue("contactId", null);
      return;
    }
    fetchContactsByCompany(companyId)
      .then((result: Contact[]) => {
        setContactGroups(buildContactGroups(result));
        setSelectedContact(null);
        setValue("contactId", null);
      })
      .catch(() => {
        toast.error("Failed to load contacts.");
        setContactGroups([]);
        setValue("contactId", null);
      });
  };

  const buildContactGroups = (contacts: Contact[]): GroupedOption[] => {
    const primary = contacts.filter((c) => c.isPrimary).map((c) => ({ value: c.id, label: c.name }));
    const secondary = contacts.filter((c) => !c.isPrimary).map((c) => ({ value: c.id, label: c.name }));
    const groups: GroupedOption[] = [];
    if (primary.length > 0) groups.push({ label: "Primary", options: primary });
    if (secondary.length > 0) groups.push({ label: "Secondary", options: secondary });
    return groups;
  };

  // On mount, restore contacts if a company was stored
  useEffect(() => {
    const storedCompanyId = initialValues.current.customerId;
    if (storedCompanyId) {
      fetchContactsByCompany(storedCompanyId)
        .then((result: Contact[]) => {
          const groups = buildContactGroups(result);
          setContactGroups(groups);
          const stored = initialValues.current.contactId;
          const allOpts = groups.flatMap((g) => g.options);
          const matchedContact = stored ? allOpts.find((o) => o.value === stored) : null;
          if (matchedContact) {
            setValue("contactId", stored);
            setSelectedContact(matchedContact);
          }
          setContactsLoaded(true);
        })
        .catch(() => {
          setContactGroups([]);
          setContactsLoaded(true);
        });
    }
  }, []);

  const closePanel = () => {
    setSavedTaskId(null);
    setSelectedCompany(null);
    setSelectedContact(null);
    setContactGroups([]);
    const defaultActivityLabel = activityGroups.flatMap((g) => g.options).find((o) => o.value === defaultActivityIdRef.current)?.label ?? "";
    reset({ subject: defaultActivityLabel, body: "", activityId: defaultActivityIdRef.current, companyId: null, companyName: "", contactId: null });
    storage.clearAll();
    onClose();
  };

  const toggleMinimise = () => {
    if (panelState === "OPEN") onMinimise();
    else onOpen();
  };

  const fetchRecentCompanies = async (): Promise<SelectOption[]> => {
    const items = await searchAutocomplete("", "Company", "Company");
    return items
      .filter((item) => item.id !== "__sep__")
      .map((item) => ({ value: item.id, label: item.label }));
  };

  const loadCompanyOptions = async (inputValue: string): Promise<GroupedOption[]> => {
    try {
      // Ensure recents are cached
      if (!recentCompaniesRef.current) {
        recentCompaniesRef.current = await fetchRecentCompanies();
      }
      const recents = recentCompaniesRef.current ?? [];

      if (!inputValue) {
        return recents.length > 0 ? [{ label: "Recent Accounts", options: recents }] : [];
      }

      // Non-empty: fetch search results then append deduped recents
      const searchResults = (await findForMultipleCompanies(inputValue)).map((item) => ({ value: item.id, label: item.label }));
      const searchIds = new Set(searchResults.map((r) => r.value));
      const dedupedRecents = recents.filter((r) => !searchIds.has(r.value));

      const groups: GroupedOption[] = [];
      if (searchResults.length > 0) groups.push({ label: "Results", options: searchResults });
      if (dedupedRecents.length > 0) groups.push({ label: "Recent Accounts", options: dedupedRecents });
      return groups;
    } catch {
      return [];
    }
  };

  const handleSave = async (values: FormValues, complete: boolean, navigateToEdit: boolean) => {
    setIsSaving(true);
    try {
      const result = await saveQuickTask({
        taskId: savedTaskId,
        customerId: values.companyId ?? null,
        contactId: values.contactId ?? null,
        subject: values.subject,
        body: values.body ?? "",

        defaultActivityId: values.activityId ?? null,
        projectId: null,
        completeTask: complete,
        redirectUrl: window.location.pathname + window.location.search,
      });

      if (!result.success) {
        toast.error(result.errorMessage ?? "Failed to save task.");
        return;
      }

      setSavedTaskId(result.taskId ?? null);
      if (result.taskId) {
        storage.saveTaskId(result.taskId);
      }

      if (navigateToEdit) {
        storage.clearAll();
        window.location.href = result.editUrl ?? "/";
        return;
      }

      toast.success(complete ? "Task completed successfully." : "Task saved successfully.");
    } catch (err: any) {
      toast.error(err?.message ?? "An error occurred.");
    } finally {
      setIsSaving(false);
    }
  };

  const onSave = handleSubmit((values) => handleSave(values, false, false));
  const onComplete = handleSubmit((values) => handleSave(values, true, false));
  const onSaveAndEdit = handleSubmit((values) => handleSave(values, false, true));

  const handleContactCreated = (contactId: string, contactName: string) => {
    if (!selectedCompany) return;
    fetchContactsByCompany(selectedCompany.value)
      .then((result: Contact[]) => {
        const groups = buildContactGroups(result);
        setContactGroups(groups);
        const newOpt = { value: contactId, label: contactName };
        setSelectedContact(newOpt);
        setValue("contactId", contactId);
        storage.saveFieldValue(storage.KEYS.contactId, contactId);
      })
      .catch(() => {
        toast.error("Failed to refresh contacts.");
      });
  };

  const handleProspectCreated = (companyId: string, companyName: string) => {
    const opt = { value: companyId, label: companyName };
    setSelectedCompany(opt);
    setValue("companyId", companyId);
    setValue("companyName", companyName);
    storage.saveFieldValue(storage.KEYS.customerIdField, companyId);
    storage.saveFieldValue(storage.KEYS.customerName, companyName);
    storage.savePanelState("OPEN", companyId);
    loadContactsForCompany(companyId);
    // Invalidate the recents cache so the new prospect appears next time
    recentCompaniesRef.current = null;
  };

  const isEditing = Boolean(savedTaskId);
  const panelTitle = isEditing ? "Task (Edit)" : "New Quick Task";

  // The panel is always in the DOM once first opened; visibility controlled by CSS
  const isVisible = panelState !== "CLOSED";
  const isMinimised = panelState === "MINIFIED";

  return (
    <div
      className={useClassNames(
        "fixed bottom-0 right-0 z-50 w-full md:right-4 md:w-96 flex flex-col rounded-t-lg shadow-2xl border border-gray-200 bg-white transition-all duration-200",
        !hasEverOpened && "hidden",
        !isVisible && "translate-y-full"
      )}
      style={{ maxHeight: isMinimised ? "2.5rem" : "min(600px, calc(100dvh - var(--layout-top-bar-height)))" }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 h-10 min-h-10 bg-(--layout-brand) text-white rounded-t-lg cursor-pointer select-none"
        onClick={toggleMinimise}
      >
        <span className="text-sm font-semibold truncate">{panelTitle}</span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="p-1 hover:bg-white/20 rounded transition-colors"
            onClick={(e) => { e.stopPropagation(); toggleMinimise(); }}
            aria-label={isMinimised ? "Expand" : "Minimise"}
          >
            {isMinimised ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          <button
            type="button"
            className="p-1 hover:bg-white/20 rounded transition-colors"
            onClick={(e) => { e.stopPropagation(); closePanel(); }}
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Body — hidden when minimised via overflow + height */}
      <div
        className="flex flex-col flex-1 overflow-hidden"
        style={{ display: isMinimised ? "none" : "flex" }}
      >
        <div className="relative flex-1 overflow-y-auto px-3 py-2 space-y-2">
          {isFormLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
              <Spinner />
            </div>
          )}
          {/* Company */}
          <div>
            <div className="flex items-center justify-between mb-0.5">
              <div className="flex items-center gap-1">
                <Label forField="qt-company" label="Company" />
                {selectedCompany && (
                  <button
                    type="button"
                    title="View company details"
                    className="p-0.5 text-muted-foreground hover:text-blue-600 transition-colors"
                    onClick={() => setIsCompanyDetailsOpen(true)}
                  >
                    <Info size={13} />
                  </button>
                )}
                {selectedCompany && (
                  <a
                    href={`${data.appRoutes.dashboard}?customerid=${selectedCompany.value}`}
                    title="Go to company"
                    className="p-0.5 text-muted-foreground hover:text-blue-600 transition-colors"
                  >
                    <ExternalLink size={13} />
                  </a>
                )}
              </div>
              <button
                type="button"
                title="Add new prospect"
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                onClick={() => setIsAddProspectOpen(true)}
              >
                <UserPlus size={13} />
                New Prospect
              </button>
            </div>
            <Controller
              name="companyId"
              control={control}
              render={({ field }) => (
                <AsyncSelect<SelectOption, false, GroupedOption>
                  inputId="qt-company"
                  loadOptions={loadCompanyOptions}
                  defaultOptions={companyDefaultOptions}
                  onMenuOpen={async () => {
                    if (companyDefaultOptions === undefined)
                      setCompanyDefaultOptions(await loadCompanyOptions(""));
                  }}
                  isClearable
                  placeholder="Type to search..."
                  value={selectedCompany}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  onChange={(opt) => {
                    const id = opt?.value ?? null;
                    const name = opt?.label ?? "";
                    setSelectedCompany(opt ?? null);
                    field.onChange(id);
                    setValue("companyName", name);
                    storage.saveFieldValue(storage.KEYS.customerIdField, id ?? "");
                    storage.saveFieldValue(storage.KEYS.customerName, name);
                    storage.savePanelState("OPEN", id ?? undefined);
                    loadContactsForCompany(id);
                  }}
                  styles={{
                    control: (base) => ({ ...base, minHeight: "36px", fontSize: "0.875rem", backgroundColor: "#F9FAFB" }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              )}
            />
          </div>

          {/* Contact */}
          <div>
            <div className="flex items-center justify-between mb-0.5">
              <div className="flex items-center gap-1">
                <Label forField="qt-contact" label="Contact" />
                {selectedContact && (
                  <button
                    type="button"
                    title="View contact details"
                    className="p-0.5 text-muted-foreground hover:text-blue-600 transition-colors"
                    onClick={() => setIsContactDetailsOpen(true)}
                  >
                    <Info size={13} />
                  </button>
                )}
              </div>
              {selectedCompany && (
                <button
                  type="button"
                  title="Add new contact"
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                  onClick={() => setIsAddContactOpen(true)}
                >
                  <UserPlus size={13} />
                  New Contact
                </button>
              )}
            </div>
            <Controller
              name="contactId"
              control={control}
              render={({ field }) => (
                <Select
                  inputId="qt-contact"
                  options={contactGroups}
                  isClearable
                  isDisabled={!selectedCompany}
                  placeholder={!selectedCompany ? "Select a company first" : contactGroups.length === 0 ? "No contacts" : "Select contact..."}
                  value={
                    contactGroups
                      .flatMap((g) => g.options)
                      .find((c) => c.value === field.value) ?? null
                  }
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  onChange={(opt) => {
                    field.onChange(opt?.value ?? null);
                    setSelectedContact(opt ?? null);
                    storage.saveFieldValue(storage.KEYS.contactId, opt?.value ?? "");
                  }}
                  styles={{
                    control: (base) => ({ ...base, minHeight: "36px", fontSize: "0.875rem", backgroundColor: "#F9FAFB" }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              )}
            />
          </div>

          {/* Activity */}
          <div>
            <Label forField="qt-activity" label="Activity" />
            <Controller
              name="activityId"
              control={control}
              rules={{ required: "Activity is required" }}
              render={({ field }) => (
                <Select
                  inputId="qt-activity"
                  options={activityGroups}
                  placeholder="Select activity..."
                  value={
                    activityGroups
                      .flatMap((g) => g.options)
                      .find((o) => o.value === field.value) ?? null
                  }
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  onChange={(opt) => {
                    field.onChange(opt?.value ?? "");
                    storage.saveFieldValue(storage.KEYS.activity, opt?.value ?? "");
                    if (opt) {
                      setValue("subject", opt.label);
                      storage.saveFieldValue(storage.KEYS.subject, opt.label);
                    }
                  }}
                  styles={{
                    control: (base) => ({ ...base, minHeight: "36px", fontSize: "0.875rem", backgroundColor: "#F9FAFB" }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              )}
            />
          </div>

          {showCalendarSyncNotice && (
            <div role="status" className="rounded border border-blue-200 bg-blue-50 p-2 text-xs text-blue-900">
              This task will sync to your calendar. Use Save &amp; Edit to change the date, time or calendar sync setting.
            </div>
          )}

          {/* Subject */}
          <div>
            <Label forField="qt-subject" label={<>Subject <span className="text-red-500">*</span></>} />
            <Input
              id="qt-subject"
              type="text"
              maxLength={100}
              inErrorState={Boolean(errors.subject)}
              {...register("subject", {
                required: "Subject is required",
                maxLength: { value: 100, message: "Subject must be 100 characters or less" },
                onChange: (e) => storage.saveFieldValue(storage.KEYS.subject, e.target.value),
              })}
            />
            <ErrorLabel error={errors.subject?.message} />
          </div>

          {/* Comments */}
          <div>
            <TextareaField
              labelFor="qt-body"
              label="Comments"
              rows={5}
              maxLength={8000}
              {...register("body", {
                onChange: (e) => storage.saveFieldValue(storage.KEYS.body, e.target.value),
              })}
            />
          </div>

        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-gray-100 bg-gray-50">
          <div className="flex items-center gap-2">
            {isSaving && <Spinner />}
            <Button
              type="button"
              variant="success"
              size="sm"
              disabled={isSaving || isFormLoading}
              onClick={onSave}
            >
              Save
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              disabled={isSaving || isFormLoading}
              onClick={onComplete}
            >
              Complete
            </Button>
          </div>
          <Button
            type="button"
            variant="default"
            size="sm"
            disabled={isSaving || isFormLoading}
            onClick={onSaveAndEdit}
          >
            Save & Edit
          </Button>
        </div>
      </div>

      <QuickProspectModal
        open={isAddProspectOpen}
        onOpenChange={setIsAddProspectOpen}
        onProspectCreated={handleProspectCreated}
      />
      {selectedCompany && (
        <QuickContactModal
          open={isAddContactOpen}
          onOpenChange={setIsAddContactOpen}
          customerId={selectedCompany.value}
          onContactCreated={handleContactCreated}
        />
      )}
      {selectedCompany && (
        <CompanyDetailsModal
          companyId={selectedCompany.value}
          companyName={selectedCompany.label}
          open={isCompanyDetailsOpen}
          onOpenChange={setIsCompanyDetailsOpen}
        />
      )}
      {selectedContact && (
        <ContactDetailsModal
          contactId={selectedContact.value}
          contactName={selectedContact.label}
          open={isContactDetailsOpen}
          onOpenChange={setIsContactDetailsOpen}
        />
      )}
    </div>
  );
};
